import React, {useEffect, useState} from 'react'
import {
    Box,
    Flex,
    SimpleGrid,
    Button,
    Text,
    useStyleConfig,
    Stack,
    useMediaQuery,
    Divider,
    useBreakpointValue,
    useDisclosure,
} from '@chakra-ui/react'
import {remCalc} from '../../theme/_utils'
import {Helmet} from 'react-helmet'
import Breadcrumb from '../breadcrumb'
import GridViewButton from '../../pages/product-list/partials/grid-view-button'
import {RecipeTile} from '../editorial-components'
import {useGetRecipesList} from './utils/use-get-recipes-list'
import {useQueryClient} from '@tanstack/react-query'
import {getSelectedFiltersFromUrl} from './utils/recipe-search-params-utils'
import {useLocation} from 'react-router-dom'
import {defineMessage, useIntl, FormattedMessage} from 'react-intl'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

//PARTIALS
import RecipeFiltersMenu from './partials/recipe-filters-menu'
import RecipesNoResults from './partials/recipes-no-results'
import RecipesFilterModal from './partials/recipes-filter-modal'
import SelectedFilters from './partials/selected-filters'
import {useCustomPreferenceWithLocale} from '../../hooks/use-custom-preference'

const RecipesList = () => {
    const filterJson = useCustomPreferenceWithLocale('kenwood_Recipes_Filters_List') || {}
    const {customParams} = getConfig()

    //translations
    const intl = useIntl()

    // ROUTER / URL
    const location = useLocation()
    const breadcrumbCategories = [
        {
            id: 'home',
            name: intl.formatMessage({
                id: 'recipes_listing_page.breadcrumbs.return_home',
                defaultMessage: 'Return to homepage',
            }),
            c_slugUrl: '/',
        },
        {
            id: 'recipes_page',
            name: intl.formatMessage({
                id: 'recipes_listing_page.breadcrumbs.recipes',
                defaultMessage: 'Recipes',
            }),
            c_slugUrl: location.pathname,
        },
    ]

    // UI STATE
    const [gridView, setGridView] = useState(false)
    const [isMobile] = useMediaQuery(`(max-width: ${remCalc(767.9)})`)
    const drawerPlacement = useBreakpointValue({
        base: 'bottom',
        small: 'bottom',
        md: 'right',
        lg: 'right',
    })
    const {isOpen, onOpen, onClose} = useDisclosure()

    // STYLES
    const styles = useStyleConfig('RecipeList')
    const productListStyles = useStyleConfig('ProductList')

    // FILTERS
    const filters = Object.values(filterJson)
    const selectedFilters = getSelectedFiltersFromUrl(location, filters)

    // DATA / QUERY
    const queryClient = useQueryClient()
    const {data, fetchNextPage, hasNextPage, isFetchingNextPage} =
        useGetRecipesList(selectedFilters)
    const recipes = data?.pages?.flat() || []

    //when the search parameters change invalidate the previous results
    const search = location.search
    useEffect(() => {
        queryClient.invalidateQueries(['recipeListQuery'])
    }, [search])

    const currentPath = 'https://' + customParams.currentHost + location.pathname
    const recipesListingPageTitleMessage = defineMessage({
        id: 'recipes.listing_page.herobanner.label',
        defaultMessage: 'Recipes Listing Page',
    })

    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: intl.formatMessage(recipesListingPageTitleMessage),
        description: intl.formatMessage(recipesListingPageTitleMessage),
        url: currentPath,
    }

    const itemListElement = recipes.map((recipe, index) => {
        const recipeTitle = recipe?.recipe_title?.replace(/[\s']+/g, '-').toLowerCase()
        const recipeUrl = `/r/recipes/${recipeTitle}/${recipe?.uid}`
        return {
            '@type': 'ListItem',
            position: index + 1,
            url: 'https://' + customParams.currentHost + recipeUrl,
        }
    })

    const itemListSchemaMarkup = {
        '@context': 'http://schema.org',
        '@type': 'ItemList',
        itemListElement,
    }

    const breadcrumbListSchemaMarkup = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbCategories.map((cat, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            item: {
                '@id': 'https://' + customParams.currentHost + (cat.c_slugUrl || ''),
                name: cat.name,
            },
        })),
    }

    return (
        <Box w="100%" h="auto">
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(itemListSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbListSchemaMarkup, null, 2)}
                </script>
            </Helmet>
            {/* Breadcrumb */}
            <Flex {...styles?.breadcrumbContainer}>
                <Breadcrumb homepage={true} categories={breadcrumbCategories} w="100%" h="100%" />
            </Flex>

            {/* Hero Banner */}
            <Flex {...styles?.herobannerContainer}>
                <Text textStyle="head-large" fontWeight="medium">
                    <FormattedMessage {...recipesListingPageTitleMessage} />
                </Text>
            </Flex>

            <Flex {...styles?.mainContentContainer}>
                <Flex {...styles?.headerContainer}>
                    <Stack {...productListStyles.refinementsStack} w="100%">
                        <Flex {...styles?.filterMenuContainer}>
                            <RecipeFiltersMenu
                                filters={filters}
                                onOpen={onOpen}
                                isMobile={isMobile}
                            />
                        </Flex>
                        {isMobile && (
                            <GridViewButton gridView={gridView} setGridView={setGridView} />
                        )}
                    </Stack>
                </Flex>
                <Divider className="recipe-list-divider" />
                <Box alignSelf="flex-start" width="100%">
                    <SelectedFilters filters={filters} />
                </Box>
                {/* Recipe Grid */}
                {recipes.length > 0 ? (
                    <SimpleGrid
                        columns={gridView ? [2, 2, 2, 4] : [1, 1, 2, 4]}
                        {...styles?.gridConfig}
                    >
                        {recipes.map((recipe, idx) => (
                            <Flex
                                key={`recipe_tile_${recipe?.uid}_${idx}`}
                                justifyContent="center"
                                w="100%"
                            >
                                <RecipeTile
                                    title={recipe?.recipe_title}
                                    uid={recipe?.uid}
                                    imageData={recipe?.recipe_media?.recipe_image?.url}
                                    difficulty={
                                        recipe?.recipe_classification?.complexity?.[0]?.title
                                    }
                                    timeToComplete={recipe?.recipe_time?.total_time?.toString()}
                                    w="100%"
                                    gridView={gridView}
                                />
                            </Flex>
                        ))}
                    </SimpleGrid>
                ) : (
                    <RecipesNoResults />
                )}
                {recipes.length > 0 && hasNextPage && (
                    <Flex {...styles?.showMoreSectionContainer}>
                        <Flex justifyContent="center">
                            <Text {...styles?.textContainer}>
                                <FormattedMessage
                                    id="recipes.listing_page.view_more_button.text_start"
                                    defaultMessage=" You are seeing"
                                />
                                <Box as="span" fontWeight="bold">
                                    {' '}
                                    {recipes.length}{' '}
                                </Box>
                                <FormattedMessage
                                    id="recipes.listing_page.view_more_button.text_end"
                                    defaultMessage=" recipes"
                                />
                            </Text>
                        </Flex>

                        <Flex w="100%" justifyContent="center">
                            <Button
                                w="100%"
                                variant="secondary"
                                onClick={() => fetchNextPage()}
                                isLoading={isFetchingNextPage}
                            >
                                <FormattedMessage
                                    id="recipes.listing_page.view_more_button.button_text"
                                    defaultMessage="View More"
                                />
                            </Button>
                        </Flex>
                    </Flex>
                )}
            </Flex>
            <RecipesFilterModal
                isOpen={isOpen}
                onClose={onClose}
                drawerPlacement={drawerPlacement}
                filters={filters}
            />
        </Box>
    )
}

export default RecipesList
